import type { Utils } from '@sparrow-marketing/contracts';
import type { ICollapseDataTypes } from '@sparrow-marketing/storybook';
import type { QuickStartSetup } from '../../states/home/types';
import type { ICollapseChildProps } from './activation-flow/components/collapsible-child';
import dataSourceIllustration from './assets/emptyDatasource.svg';
import reportIllustration from './assets/emptyReport.svg';

type IQuickStartSetupSteps = Utils.DynamicObject<
  Exclude<QuickStartSetup, 'dashboard'>,
  Partial<ICollapseDataTypes>
>;

type ICollapsibleChild = Utils.DynamicObject<
  Exclude<QuickStartSetup, 'account'>,
  Omit<ICollapseChildProps, 'currentIndex'>
>;

export const HELP_DOCS = {
  datasources:
    'https://support.viewmetrics.com/en/article/how-to-connect-a-data-source-1bs5g7j/',
  destination:
    'https://support.viewmetrics.com/en/article/connecting-destinations-f6254g/',
  report: 'https://support.viewmetrics.com/en/article/creating-reports-tzmpvq/',
  global: 'https://support.sparrowcharts.com/',
};

export const QUICK_START_SETUP_STEPS: IQuickStartSetupSteps = {
  account: {
    heading: 'Setup your account',
  },
  datasources: {
    heading: 'Connect a data source',
  },
  destination: {
    heading: 'Connect your Google Drive',
  },
  report: {
    heading: 'Create your first report',
  },
};

export const COLLAPSIBLE_CHILD_COMPONENT: ICollapsibleChild = {
  datasources: {
    text:
      'Connect your marketing data sources (social media, ads, email, website) to ViewMetrics to start seeing analytics, creating reports, and creating embeddable charts.',
    video: {
      src: 'https://i.ytimg.com/vi/BlXeBXt6tEI/maxresdefault.jpg',
      title: 'How to connect a data source',
      link: 'https://www.youtube.com/watch?v=BlXeBXt6tEI',
    },
    cta: 'Connect data source',
    learnMore: HELP_DOCS.datasources,
  },
  destination: {
    text:
      'To be able to generate exportable reports from ViewMetrics, you will first need to connect your Google Drive account. This allows ViewMetrics to save generated reports to folders you select, and will allow you to export reports as Google Slides/Sheets reports so they are fully customizable.',
    subText: 'Learn more about requested permissions',
    video: {
      src: 'https://i.ytimg.com/vi/D7w482h0ilw/maxresdefault.jpg',
      title: 'Connecting Google Drive',
      link: 'https://www.youtube.com/watch?v=D7w482h0ilw',
    },
    cta: 'Sign in with Google',
    destination: true,
    learnMore: HELP_DOCS.destination,
  },
  report: {
    text: (
      <>
        Select a report template, choose your data source, and tweak the time
        range and settings to get a performance template branded with your logo
        and marketing data. <br /> <br /> Generated reports will get saved to
        your connected Google Drive.
      </>
    ),
    video: {
      src: 'https://i.ytimg.com/vi/CGB40VStcPA/maxresdefault.jpg',
      title: 'How to create a report',
      link: 'https://www.youtube.com/watch?v=CGB40VStcPA&t=26s',
    },
    cta: 'Create report',
    learnMore: HELP_DOCS.report,
  },
  dashboard: {
    text: '',
    video: {
      src: 'https://i.ytimg.com/vi/CGB40VStcPA/maxresdefault.jpg',
      title: 'How to create a report',
      link: 'https://www.youtube.com/watch?v=CGB40VStcPA&t=26s',
    },
    cta: 'Watch video',
    learnMore: '',
  },
};

export const DASHBOARD_EMPTY_CARDS = {
  datasource: {
    description: 'No sources connected',
    illustration: dataSourceIllustration,
    btnLabel: 'Add data source',
  },
  reports: {
    description: 'No reports created',
    illustration: reportIllustration,
    btnLabel: 'Create report',
  },
};

export const DASHBOARD_INVITE_USER = {
  contactSupport: {
    title: 'Need more capacity?',
    description:
      'Feel free to contact support if you need more data sources or team slots.',
    btnLabel: 'Contact support',
    benefits: [
      { text: '$6.50/mo for every extra data source', icon: 'info-outline' },
      { text: '$6.50/mo for every extra team slot', icon: 'info-outline' },
    ],
  },
  upgradeToNextTier: {
    title: 'You’ll need to upgrade your plan',
    description: 'Upgrade your plan to access:',
    btnLabel: 'Upgrade',
    benefits: [
      { text: 'Connect up to 50 data sources', icon: 'circular-green-tick' },
      { text: 'Unlimited workspaces', icon: 'circular-green-tick' },
      { text: 'Unbranded embed charts', icon: 'circular-green-tick' },
      { text: 'Invite up to 10 team members', icon: 'circular-green-tick' },
    ],
  },

  upgradeToPro: {
    title: 'You’ll need to upgrade to Pro plan',
    description: 'Upgrade your plan to Pro to access:',
    btnLabel: 'Upgrade',
    benefits: [
      { text: 'Up to 20 data sources', icon: 'circular-green-tick' },
      { text: 'Unlimited workspaces', icon: 'circular-green-tick' },
      { text: 'Unbranded embed charts', icon: 'circular-green-tick' },
    ],
  },
  upgradeToTeam: {
    title: 'You’ll need to upgrade to Team plan',
    description: 'Upgrade your plan to Team to access:',
    btnLabel: 'Upgrade',
    benefits: [
      { text: 'Connect up to 50 data source', icon: 'circular-green-tick' },
      { text: 'Unlimited workspaces', icon: 'circular-green-tick' },
      { text: 'Unbranded embed charts', icon: 'circular-green-tick' },
      { text: 'Invite up to 10 team members', icon: 'circular-green-tick' },
    ],
  },
};

export const PLAN_INDEX_OBJ: { [key: string]: number } = {
  Free: 1,
  Starter: 2,
};

export const MODEL_KEY_OBJ: {
  [key: string]: keyof typeof DASHBOARD_INVITE_USER;
} = {
  Free: 'upgradeToNextTier',
  Starter: 'upgradeToNextTier',
  Pro: 'upgradeToTeam',
};
